import { useContext, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { SpacingContainer, DatasetStatus } from '@components'
import { submissionContext } from '@contexts'
import {
  DatasetDataContext,
  ActionType as DatasetDataActionType,
} from '@datasets/dataset-details/datasetData/context'
import { Status } from '@datasets/dataset-details/header/components/status'
import { useDatasetQueryContext } from '@datasets/dataset-query-context/hooks'
import { Submission } from '@global/types'
import { ApprovalContext } from '@templates/approval/contexts'
import { RowModeSelector, ToggleColumns, FilterRows } from './components'
import { IntegrationLastSync } from '../IntegrationLastSync'
import * as css from './styles'
import { ReadGridToolbarContext } from '../context/toolbar'
import { RootState } from '@redux/store'
import { IDatasetMetadataResponse } from '@global/api/types'

interface Props {
  dataset?: IDatasetMetadataResponse
}

export const ReadGridToolbar = ({ dataset }: Props) => {
  const { dispatch: dispatchDatasetData, rowMode } = useContext(DatasetDataContext)
  const { gridRef } = useContext(ReadGridToolbarContext)
  const location = useLocation()
  const isSubmissionDataPage = /codes\/details\/[0-9]*\/data.*/.test(location.pathname)
  const datasetQueryContext = useDatasetQueryContext()
  const hasDatasetQueryContext = Boolean(datasetQueryContext)
  const hasApprovalContext = Boolean(useContext(ApprovalContext))

  const features = useSelector((state: RootState) => state.Auth.features)
  const canSeeDatasetQueryOptions = Boolean(features['dataset-query-options'])
  const canSeePendingFilters = Boolean(features['pending-rollup-filters'])
  const showFilterOption =
    canSeeDatasetQueryOptions && hasDatasetQueryContext && !hasApprovalContext

  const { byId } = useContext(submissionContext)
  const submission: Submission | null = (byId[dataset?.id] as Submission | null) ?? null
  const submissionStatus = submission?.status ?? submission?.latest_submission_status
  const isPendingStatus = Object.values(DatasetStatus).includes(submissionStatus as DatasetStatus)
  const isPendingRollup = submission?.is_pending_rollup ?? false

  const onRowModeChange = useCallback(
    (rowModeParam: string) => {
      dispatchDatasetData({ type: DatasetDataActionType.setRowMode, payload: rowModeParam })
      const hotTable = gridRef?.current as {
        recomputeGridSize?: (options: { rowIndex: number; columnIndex: number }) => void
      }
      hotTable.recomputeGridSize?.({ rowIndex: 0, columnIndex: 0 })
    },
    [gridRef?.current],
  )

  return (
    <css.Container>
      <SpacingContainer css={css.lastSavedContainer}>
        {submission && <Status submission={submission} datasetMeta={dataset} />}
        {isPendingStatus && <IntegrationLastSync datasetId={Number(dataset?.id)} />}
      </SpacingContainer>

      <SpacingContainer css={css.viewOptionsContainer}>
        {showFilterOption && <FilterRows />}
        <ToggleColumns
          datasetId={Number(dataset?.id)}
          isSubmissionPage={isSubmissionDataPage && !(isPendingRollup && canSeePendingFilters)}
          templateId={Number(dataset?.template_id)}
        />
      </SpacingContainer>

      <SpacingContainer css={css.rowModeSelector}>
        <RowModeSelector rowMode={rowMode} onRowModeChange={onRowModeChange} />
      </SpacingContainer>
    </css.Container>
  )
}
